import { useEffect, useState, useRef } from 'react';
import '../css/CreativesPage.css';
import { Plus, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import CreativeCard from "../components/creative/CreativeCard";

import { io } from 'socket.io-client';
import { SOCKET_BASE_URL } from '../api/apiConstants';
import { useToast } from '../context/ToastContext';

export default function CreativesPage() {
  const [creatives, setCreatives] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [progressMap, setProgressMap] = useState({});
  const [statusMap, setStatusMap] = useState({});
  const [bunnyURLMap, setBunnyURLMap] = useState({});

  const { success, error } = useToast();

  const socketRef = useRef(null);
  const joinedRooms = useRef(new Set());

  const joinRoom = (rooms) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) return;

    rooms.forEach((c) => {
      const id = String(c.id);
      if (c.status === "inprogress" && !joinedRooms.current.has(id)) {
        socket.emit("join-room", id);
        joinedRooms.current.add(id);
      }
    });
  };

  useEffect(() => {
    joinedRooms.current.clear(); // ✅ IMPORTANT
  }, []);

  useEffect(() => {
    socketRef.current = io(SOCKET_BASE_URL, {
      transports: ["websocket", "polling"],
    });

    const socket = socketRef.current;

    socket.on("connect", () => {
      console.log("✅ Connected:", socket.id);
    });

    socket.on("disconnect", () => {
      console.log("❌ Disconnected");
    });

    socket.on("videoStatus", ({ id, progress, status, bunny_url }) => {
      id = String(id);

      if (progress !== undefined) {
        setProgressMap(prev => ({ ...prev, [id]: progress }));
      }

      if (status) {
        setStatusMap(prev => ({ ...prev, [id]: status }));

        if (status === "inprogress" && !joinedRooms.current.has(id)) {
          socket.emit("join-room", id);
          joinedRooms.current.add(id);
        }
      }

      if (bunny_url) {
        setBunnyURLMap(prev => ({ ...prev, [id]: bunny_url }));
      }
    });

    socket.on("completed", ({ id }) => {
      setProgressMap(prev => ({ ...prev, [id]: 100 }));
      setStatusMap(prev => ({ ...prev, [id]: "completed" }));
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;

    if (!socket) return;
    if (!creatives.length) return;

    if (socket.connected) {
      joinRoom(creatives);
    } else {
      const handleConnect = () => {
        joinRoom(creatives);
      };
      socket.once("connect", handleConnect);
      return () => {
        socket.off("connect", handleConnect);
      };
    }
  }, [creatives]);

  useEffect(() => {
    getCreative();
  }, []);

  const getCreative = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/generate-video/list`);
      if (response && response.data && response.data.data) {
        const data = response.data.data;
        setCreatives(data);
        joinRoom(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCreative = (creative) => {
    setDeleteTarget(creative);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;

    try {
      setIsDeleting(true);
      await api.delete(`/creative/${id}`);
      getCreative();
      setDeleteTarget(null);
      success('Creative deleted successfully');
    } catch (err) {
      console.error("Delete failed", err);
      error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setIsDeleting(false);
    }
  };


  return (
    <div className="creatives-page">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="premium-page-title">Creatives</h1>
          <p className="text-muted-foreground">Manage all your generated CTV creatives</p>
        </div>
        <Link to="/dashboard/editor" className="dashboard-btn-new hover:-translate-y-px transition-transform duration-150 ease-in-out">
          <span className="dashboard-btn-plus">
            <Plus className="h-4 w-4" />
          </span> New Creative
        </Link>
      </div>

      <div className="creatives-toolbar mt-3 hidden">
        <div className="creatives-search">
          <span className="creatives-search-icon">
            <Search className="h-4 w-4 text-muted-foreground" />
          </span>
          <input
            type="text"
            placeholder="Search creatives..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="creatives-search-input"
          />
        </div>
        <div className="creatives-actions">
          <button type="button" className="creatives-btn creatives-btn-outline">
            Filters
          </button>
          <button type="button" className="creatives-btn creatives-btn-outline">
            Export Selected
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
        </div>
      ) : creatives.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[80vh] text-gray-500">
          <p className="text-lg font-medium">No creatives yet</p>
          <p className="text-sm">Create your first creative to get started</p>
          <Link
            to="/dashboard/editor"
            className="dashboard-btn-new hover:-translate-y-px transition-transform duration-150 ease-in-out"
          >
            <span className="dashboard-btn-plus">
              <Plus className="h-4 w-4" />
            </span>
            New Creative
          </Link>
        </div>
      ) : (
        <div className="mt-5 grid gap-7 md:grid-cols-2 xl:grid-cols-3">
          {creatives.map((c) => (
            <CreativeCard key={c.id} c={c}
              progress={progressMap[c.id] || 0}
              status={statusMap[c.id] || c.status}
              bunnyURL={bunnyURLMap[c.id] || c.bunny_url}
              onDelete={handleDeleteCreative}
            />
          ))}
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1111]">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 animate-fadeIn">

            <h2 className="text-2xl font-semibold mb-2">
              Delete Creative
            </h2>

            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-medium text-gray-900">
                {deleteTarget.creative_name}
              </span>
              ? This action cannot be undone.
            </p>

            <div className="mt-6 flex justify-end gap-3">

              <button
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
              >
                {isDeleting ? <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Deleting...
                </>
                  : "Delete"
                }
              </button>

            </div>
          </div>
        </div>
      )}


    </div>
  );
}
